import type { Brand } from './brand';

/**
 * The FRESHNESS axis — a separate axis from presence, with its own brand.
 *
 * DERIVED FROM, VERBATIM:
 *   backend/contracts/src/main/java/org/rescue/contract/api/readmodel/Freshness.java
 *     enum Freshness { FRESH, STALE, UNKNOWN, DEGRADED }
 *   blob 54f58f1244fa615792413fd361311171d02ba310
 *   whose own documentation states the load-bearing rule:
 *     "Read-model freshness semantics; stale inputs do not become ALLOW."
 *
 * WHY THIS AXIS CARRIES THE SAME WORD `UNKNOWN` AS THE PRESENCE AXIS, AND WHY
 * THAT IS THE MOST DANGEROUS SPOT IN THIS PACKAGE
 *
 *   `Freshness.UNKNOWN` and `Status.UNKNOWN` are DIFFERENT STATES that happen to
 *   share a spelling in the frozen contract:
 *
 *     presence UNKNOWN   — the read plane resolved, and the subject's data is
 *                          absent. It is an answer.
 *     freshness UNKNOWN  — an answer was obtained, and how current it is cannot
 *                          be established. It is a caveat on an answer.
 *
 *   Collapsing them would let an un-ageable answer render as "no data", or let
 *   "no data" acquire a freshness. The two are branded differently here, so
 *   `Extract<PresenceUnknown, FreshnessUnknown>` is `never` and the assignment
 *   does not compile. `./invariants.ts` asserts it.
 *
 * FRESHNESS NEVER DEGRADES INTO PERMISSION. A STALE or DEGRADED input must not
 * become an ALLOW, an available, or a success (builder registry SEMANTIC
 * PRESERVATION; the frozen enum's own comment).
 */

/** The answer is current. */
export type FreshnessFresh = Brand<'FRESH', 'FreshnessAxis'>;

/** The answer is aged. NOT an error, NOT a reason to proceed. */
export type FreshnessStale = Brand<'STALE', 'FreshnessAxis'>;

/** How current the answer is cannot be established. NOT an absent answer. */
export type FreshnessUnknown = Brand<'UNKNOWN', 'FreshnessAxis'>;

/** The answer is current but from a diminished source. NOT an absence. */
export type FreshnessDegraded = Brand<'DEGRADED', 'FreshnessAxis'>;

/** The closed freshness vocabulary. */
export type Freshness = FreshnessFresh | FreshnessStale | FreshnessUnknown | FreshnessDegraded;

/**
 * The four freshness values. The only construction site for the vocabulary.
 */
export const Freshness = Object.freeze({
  FRESH: 'FRESH' as FreshnessFresh,
  STALE: 'STALE' as FreshnessStale,
  UNKNOWN: 'UNKNOWN' as FreshnessUnknown,
  DEGRADED: 'DEGRADED' as FreshnessDegraded,
});
